/**
 * PLC 代码编辑器组件
 *
 * 基于 Monaco Editor 实现，支持：
 * - ST/IL 语法高亮和自动补全
 * - LAD/FBD 图形化语言占位提示
 * - 代码格式化、复制、下载
 * - 与 AI 对话的代码交互
 */

import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle
} from 'react';
import Editor, {OnMount, OnChange, DiffEditor, loader} from '@monaco-editor/react';
import * as monacoInstance from 'monaco-editor';
import type * as Monaco from 'monaco-editor';

// 强制使用本地安装的 monaco-editor，禁止从 CDN (jsdelivr) 动态加载
// 原因：@monaco-editor/loader 默认会从 https://cdn.jsdelivr.net/npm/monaco-editor@x.x.x 加载，
// 网络不稳定时会导致编辑器无法加载
loader.config({ monaco: monacoInstance });

import {PlcEditorToolbar, ExportFormat} from './PlcEditorToolbar';
import { DiffReviewToolbar } from './DiffReviewToolbar';
import {PlcTestDrawer} from '../PlcTestPanel';
import {
  registerPlcLanguages,
  registerPlcTheme,
  getMonacoLanguageId
} from './PlcLanguageConfig';
import {plcCodeService} from '../../services/plcCodeService';
import {
  PlcLanguageType,
  PlcCodeEditorProps,
  PlcEditorConfig,
  DEFAULT_EDITOR_CONFIG,
  OnSelectionChangeCallback,
  CodeSelection,
  DiffReviewState
} from '../../types';

// ============================================
// 类型定义
// ============================================

/** 编辑器暴露给父组件的方法 */
export interface PlcCodeEditorRef {
  /** 获取当前代码 */
  getCode: () => string;
  /** 设置代码内容 */
  setCode: (code: string) => void;
  /** 格式化代码 */
  format: () => void;
  /** 聚焦编辑器 */
  focus: () => void;
  /** 获取 Monaco 编辑器实例 */
  getEditorInstance: () => Monaco.editor.IStandaloneCodeEditor | null;
  /** 清除编辑器选区 */
  clearSelection: () => void;
}

/** 扩展的编辑器 Props，增加选区回调和 diff 审查 */
interface PlcCodeEditorInternalProps extends PlcCodeEditorProps {
  /** 选区变更回调 */
  readonly onSelectionChange?: OnSelectionChangeCallback;
  /** diff 审查状态（传入后进入 diff 模式） */
  readonly diffReviewState?: DiffReviewState | null;
  /** 接受变更回调 */
  readonly onAcceptChanges?: () => void;
  /** 拒绝变更回调 */
  readonly onRejectChanges?: () => void;
}

// ============================================
// 图形化语言占位组件
// ============================================

interface GraphicalPlaceholderProps {
  readonly language: PlcLanguageType;
}

const GraphicalPlaceholder: React.FC<GraphicalPlaceholderProps> = ({
  language
}) => {
  const info = useMemo(() => {
    if (language === 'lad') {
      return {
        icon: 'sitemap',
        title: '梯形图 (LAD)',
        desc: '梯形图是一种图形化 PLC 编程语言，模拟电气控制线路的外观。当前版本暂不支持图形化编辑，请使用专业 PLC 编程软件（如 TIA Portal、CODESYS）进行梯形图编程。'
      };
    }
    return {
      icon: 'project-diagram',
      title: '功能块图 (FBD)',
      desc: '功能块图是一种图形化 PLC 编程语言，使用功能块和连线表示程序逻辑。当前版本暂不支持图形化编辑，请使用专业 PLC 编程软件进行 FBD 编程。'
    };
  }, [language]);

  return (
    <div className="plc-graphical-placeholder">
      <i className={`fa fa-${info.icon} plc-graphical-icon`} />
      <h3 className="plc-graphical-title">{info.title}</h3>
      <p className="plc-graphical-desc">{info.desc}</p>
    </div>
  );
};

// ============================================
// 加载遮罩组件
// ============================================

interface LoadingOverlayProps {
  readonly visible: boolean;
  readonly text?: string;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  visible,
  text = 'AI 正在生成代码...'
}) => {
  if (!visible) {
    return null;
  }

  return (
    <div className="plc-editor-loading-overlay">
      <div className="plc-loading-spinner" />
      <span className="plc-loading-text">{text}</span>
    </div>
  );
};

// ============================================
// 语言名映射（diff 工具栏用）
// ============================================

const LANG_LABELS: Readonly<Record<PlcLanguageType, string>> = {
  st: 'ST - 结构化文本',
  il: 'IL - 指令表',
  lad: 'LAD - 梯形图',
  fbd: 'FBD - 功能块图'
};

// ============================================
// 行数计算工具
// ============================================

const countLines = (text: string): number => {
  if (!text) return 0;
  return text.split('\n').length;
};

// ============================================
// 主组件
// ============================================

export const PlcCodeEditor = forwardRef<PlcCodeEditorRef, PlcCodeEditorInternalProps>(
  (
    {
      value,
      onChange,
      language,
      onLanguageChange,
      config,
      showToolbar = true,
      className = '',
      loading = false,
      onSelectionChange,
      diffReviewState,
      onAcceptChanges,
      onRejectChanges
    },
    ref
  ) => {
    // Monaco 编辑器实例引用
    const editorRef = useRef<Monaco.editor.IStandaloneCodeEditor | null>(null);
    const monacoRef = useRef<typeof Monaco | null>(null);

    // ✨ 新增：用 ref 保存最新的回调和语言，避免闭包陷阱
    const onSelectionChangeRef = useRef(onSelectionChange);
    const languageRef = useRef(language);

    const [testDrawerVisible, setTestDrawerVisible] = useState(false);

    // 每次渲染时同步最新值到 ref
    useEffect(() => {
      onSelectionChangeRef.current = onSelectionChange;
    }, [onSelectionChange]);

    useEffect(() => {
      languageRef.current = language;
    }, [language]);

    // 合并配置
    const mergedConfig: PlcEditorConfig = useMemo(
      () => ({
        ...DEFAULT_EDITOR_CONFIG,
        ...config
      }),
      [config]
    );

    // 判断是否为图形化语言
    const isGraphical = language === 'lad' || language === 'fbd';

    // ✨ 新增：判断是否处于 diff 审查模式
    const isDiffMode = diffReviewState != null && diffReviewState.status === 'reviewing';

    // ========================================
    // DiffEditor 挂载回调
    // ========================================
    const handleDiffEditorMount = useCallback(
      (editor: Monaco.editor.IStandaloneDiffEditor, monaco: typeof Monaco) => {
        // 注册语言和主题（DiffEditor 也需要）
        registerPlcLanguages(monaco);
        registerPlcTheme(monaco);
        monaco.editor.setTheme('plc-dark');
      },
      []
    );

    // ========================================
    // 编辑器挂载回调(修复：失焦时不清除选区)
    // ========================================
    const handleEditorMount: OnMount = useCallback((editor, monaco) => {
      editorRef.current = editor;
      monacoRef.current = monaco;

      // 注册 PLC 语言和主题
      registerPlcLanguages(monaco);
      registerPlcTheme(monaco);

      // 应用主题
      monaco.editor.setTheme('plc-dark');

      // 添加快捷键
      editor.addCommand(
        monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyF,
        () => {
          handleFormat();
        }
      );

      // ✨ 监听选区变化
      editor.onDidChangeCursorSelection((e) => {
        const cb = onSelectionChangeRef.current;
        if (!cb) {
          return;
        }

        // ✨ 关键修复：如果编辑器没有焦点（用户点击了其他地方），
        // 不触发选区清除，保留上一次的选区状态
        if (!editor.hasTextFocus()) {
          return;
        }

        const selection = e.selection;
        const model = editor.getModel();
        if (!model) {
          return;
        }

        const selectedText = model.getValueInRange(selection);

        // 无选中文本 → 清空选区
        if (!selectedText.trim()) {
          cb(null);
          return;
        }

        const totalLines = model.getLineCount();
        const startLine = selection.startLineNumber;
        const endLine = selection.endLineNumber;

        // 判断是否全选
        const isFullSelection =
          startLine === 1 &&
          endLine === totalLines &&
          selectedText.trim() === model.getValue().trim();

        const codeSelection: CodeSelection = {
          code: selectedText,
          startLine,
          endLine,
          language,
          isFullSelection
        };

        cb(codeSelection);
      });
    }, []);// 只在挂载时注册一次，通过 ref 获取最新值

    // ========================================
    // 代码变更回调
    // ========================================
    const handleEditorChange: OnChange = useCallback(
      newValue => {
        onChange(newValue ?? '');
      },
      [onChange]
    );

    // ========================================
    // 工具栏操作
    // ========================================

    /** 格式化代码 */
    const handleFormat = useCallback((): void => {
      if (!editorRef.current || isGraphical) {
        return;
      }

      const currentCode = editorRef.current.getValue();
      const formattedCode = plcCodeService.formatPlcCode(currentCode, language);

      // 保持光标位置
      const position = editorRef.current.getPosition();
      editorRef.current.setValue(formattedCode);
      if (position) {
        editorRef.current.setPosition(position);
      }

      onChange(formattedCode);
    }, [language, onChange, isGraphical]);

    /** 复制代码 */
    const handleCopy = useCallback(async (): Promise<void> => {
      try {
        await navigator.clipboard.writeText(value);
      } catch (err) {
        // 降级方案：使用 execCommand
        const textarea = document.createElement('textarea');
        textarea.value = value;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
    }, [value]);

    /** 导出代码，根据格式配置处理换行符和文件后缀 */
    const handleDownload = useCallback((format: ExportFormat): void => {
      // 统一先将换行符标准化为 LF，再根据目标格式转换
      const normalizedCode = value.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      const outputCode = format.lineEnding === 'crlf'
        ? normalizedCode.replace(/\n/g, '\r\n')
        : normalizedCode;

      const blob = new Blob([outputCode], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `plc_code_${Date.now()}${format.extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    }, [value]);

    /** 撤销 */
    const handleUndo = useCallback((): void => {
      editorRef.current?.trigger('keyboard', 'undo', null);
    }, []);

    /** 重做 */
    const handleRedo = useCallback((): void => {
      editorRef.current?.trigger('keyboard', 'redo', null);
    }, []);

    /** 发送代码到 AI */
    const handleSendToAI = useCallback((): void => {
      // 触发自定义事件，由父组件处理
      const event = new CustomEvent('plc-send-to-ai', {
        detail: {code: value, language}
      });
      window.dispatchEvent(event);
    }, [value, language]);

    // ========================================
    // 暴露方法给父组件
    // ========================================
    useImperativeHandle(
      ref,
      () => ({
        getCode: (): string => {
          return editorRef.current?.getValue() ?? value;
        },
        setCode: (code: string): void => {
          if (editorRef.current) {
            editorRef.current.setValue(code);
          }
          onChange(code);
        },
        format: handleFormat,
        focus: (): void => {
          editorRef.current?.focus();
        },
        getEditorInstance: () => editorRef.current,
        clearSelection: (): void => {
          if (editorRef.current) {
            const position = editorRef.current.getPosition();
            if (position) {
              editorRef.current.setSelection({
                startLineNumber: position.lineNumber,
                startColumn: position.column,
                endLineNumber: position.lineNumber,
                endColumn: position.column
              });
            }
          }
        }
      }),
      [value, onChange, handleFormat]
    );

    // ========================================
    // 编辑器配置选项
    // ========================================
    const editorOptions: Monaco.editor.IStandaloneEditorConstructionOptions =
      useMemo(
        () => ({
          fontSize: mergedConfig.fontSize,
          tabSize: mergedConfig.tabSize,
          wordWrap: mergedConfig.wordWrap,
          lineNumbers: mergedConfig.lineNumbers,
          minimap: {enabled: mergedConfig.minimap},
          readOnly: mergedConfig.readOnly || loading,
          automaticLayout: true,
          scrollBeyondLastLine: false,
          folding: true,
          foldingStrategy: 'indentation',
          renderLineHighlight: 'all',
          selectOnLineNumbers: true,
          roundedSelection: true,
          cursorBlinking: 'smooth',
          cursorSmoothCaretAnimation: true, // ✨ 0.30.x 中是 boolean，不是 'on'
          smoothScrolling: true,
          contextmenu: true,
          fontFamily: "'JetBrains Mono', 'Fira Code', Consolas, monospace",
          fontLigatures: true,
          padding: {top: 16, bottom: 16}
        }),
        [mergedConfig, loading]
      );

    // ✨ 新增：diff 编辑器选项
    const diffEditorOptions: Monaco.editor.IDiffEditorConstructionOptions = useMemo(
      () => ({
        fontSize: mergedConfig.fontSize,
        readOnly: true,
        renderSideBySide: true,
        automaticLayout: true,
        scrollBeyondLastLine: false,
        folding: true,
        fontFamily: "'JetBrains Mono', 'Fira Code', Consolas, monospace",
        fontLigatures: true,
        padding: { top: 16, bottom: 16 },
        originalEditable: false,
        enableSplitViewResizing: true,
        renderIndicators: true,
        renderMarginRevertIcon: false
      }),
      [mergedConfig.fontSize]
    );

    // ==============测试抽屉相关回调========================
    const handleOpenTest = useCallback(() => {
      setTestDrawerVisible(true);
    }, []);

    const handleCloseTest = useCallback(() => {
      setTestDrawerVisible(false);
    }, []);

    // ========================================
    // 渲染
    // ========================================
    return (
      <div className={`plc-coder-editor ${className}`}>
        {/* 工具栏：diff 模式用 DiffReviewToolbar，否则用普通工具栏 */}
        {isDiffMode ? (
          <DiffReviewToolbar
            originalLineCount={countLines(diffReviewState.originalCode)}
            incomingLineCount={countLines(diffReviewState.incomingCode)}
            languageLabel={LANG_LABELS[diffReviewState.incomingLanguage] ?? language}
            onAccept={onAcceptChanges ?? (() => {})}
            onReject={onRejectChanges ?? (() => {})}
          />
        ) : (
          showToolbar && (
            <PlcEditorToolbar
              language={language}
              onLanguageChange={onLanguageChange}
              isDirty={false}
              isLoading={loading}
              onFormat={handleFormat}
              onCopy={handleCopy}
              onDownload={handleDownload}
              onUndo={handleUndo}
              onRedo={handleRedo}
              onSendToAI={handleSendToAI}
              onOpenTest={handleOpenTest}
            />
          )
        )}

        {/* 编辑器容器 */}
        <div className="plc-monaco-container">
          {isGraphical ? (
            <GraphicalPlaceholder language={language} />
          ) : isDiffMode ? (
            /* ✨ Diff 模式：使用 Monaco DiffEditor */
            <div className="plc-monaco-wrapper plc-diff-wrapper">
              <DiffEditor
                height="100%"
                language={getMonacoLanguageId(diffReviewState.incomingLanguage)}
                original={diffReviewState.originalCode}
                modified={diffReviewState.incomingCode}
                onMount={handleDiffEditorMount}
                options={diffEditorOptions}
                theme="vs-dark"
                loading={
                  <div className="plc-editor-loading-overlay">
                    <div className="plc-loading-spinner" />
                    <span className="plc-loading-text">对比加载中...</span>
                  </div>
                }
              />
            </div>
          ) : (
            /* 普通模式：Monaco Editor */
            <div className="plc-monaco-wrapper">
              <Editor
                height="100%"
                language={getMonacoLanguageId(language)}
                value={value}
                onChange={handleEditorChange}
                onMount={handleEditorMount}
                options={editorOptions}
                theme="vs-dark"
                loading={
                  <div className="plc-editor-loading-overlay">
                    <div className="plc-loading-spinner" />
                    <span className="plc-loading-text">编辑器加载中...</span>
                  </div>
                }
              />
            </div>
          )}

          {/* 加载遮罩（非 diff 模式下显示） */}
          {!isDiffMode && <LoadingOverlay visible={loading} />}
        </div>
        {/* 测试抽屉 */}
        <PlcTestDrawer
          visible={testDrawerVisible}
          onClose={handleCloseTest}
          code={value}
          language={language}
        />
      </div>
    );
  }
);

PlcCodeEditor.displayName = 'PlcCodeEditor';

export default PlcCodeEditor;
